import React, { useState, useEffect } from 'react';
import { RefreshCw, Settings as SettingsIcon, Database, ShoppingCart, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

export function AmazonSettings({ storeId }) {
  const [syncing, setSyncing]         = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [storeInfo, setStoreInfo]     = useState(null);

  const activeStoreId = storeId || localStorage.getItem('amazon_store_id');

  useEffect(() => {
    if (activeStoreId) fetchStoreInfo();
  }, [activeStoreId]);

  const fetchStoreInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/amazon/stores/${activeStoreId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStoreInfo(data.store);
    } catch {}
  };

  const handleFullSync = async () => {
    try {
      setSyncing(true);
      toast.info('Starting full sync...');
      const token = localStorage.getItem('token');

      // Step 1: Sync products (gets latest listings + costs from Amazon)
      toast.info('Syncing products...');
      const productsRes = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/products/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const productsData = await productsRes.json();
      if (productsData.success) {
        toast.success(`Synced ${productsData.synced} products with latest costs`);
      } else {
        toast.error(`Product sync failed: ${productsData.error}`);
      }

      // Step 2: Sync orders (incremental — only new orders since last sync)
      toast.info('Syncing orders...');
      const ordersRes = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/orders/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_full: false })
      });
      const ordersData = await ordersRes.json();
      if (ordersData.success) {
        toast.success(`Synced ${ordersData.synced} order items`);
      } else {
        toast.error(`Order sync failed: ${ordersData.error}`);
      }

      toast.success('Full sync completed!');
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleBackfillCosts = async () => {
    try {
      setBackfilling(true);
      toast.info('Backfilling historical costs...');
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/orders/backfill-costs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Updated ${data.updated} historical orders with correct costs!`);
      } else {
        toast.error('Backfill failed');
      }
    } catch {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Amazon Settings</h1>

      {/* Store Connection */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <ShoppingCart className="text-orange-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Amazon Seller Central Connection</h2>
        </div>
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
          <p className="text-orange-800 font-medium">✓ Connected to Amazon</p>
          {storeInfo && (
            <div className="mt-2 space-y-1">
              <p className="text-orange-700 text-sm">Store: <strong>{storeInfo.store_name}</strong></p>
              <p className="text-orange-700 text-sm">Seller ID: {storeInfo.seller_id}</p>
              <p className="text-orange-700 text-sm">Marketplace: {storeInfo.marketplace_id} (UK)</p>
              <p className="text-orange-700 text-sm">Region: {storeInfo.region}</p>
            </div>
          )}
        </div>
      </div>

      {/* Sync */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <RefreshCw className="text-orange-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Synchronization</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Syncs all products with latest costs from Amazon, then syncs new orders.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ First run fetches all orders (last 30 days). After that, only new orders are fetched.
        </p>
        <button
          onClick={handleFullSync}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Run Full Sync'}
        </button>
        <p className="text-gray-500 text-sm mt-3">
          ℹ️ Automatic sync runs in the background periodically
        </p>
      </div>

      {/* Backfill */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <DollarSign className="text-orange-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Backfill Historical Costs</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Updates cost price and net profit for all existing Amazon orders using the latest product costs.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ Run this once after your first full sync to fix profit data on all historical orders.
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-4">
          <p className="text-yellow-800 text-sm font-medium">
            ⚠️ Make sure to run "Run Full Sync" first so product costs are up to date before backfilling.
          </p>
        </div>
        <button
          onClick={handleBackfillCosts}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50"
        >
          <DollarSign className={`mr-2 ${backfilling ? 'animate-spin' : ''}`} size={20} />
          {backfilling ? 'Backfilling...' : 'Backfill Historical Costs'}
        </button>
      </div>

      {/* Database */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <Database className="text-purple-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Database</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Database Status</span>
            <span className="font-semibold text-green-600">Connected</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Store ID</span>
            <span className="font-semibold text-gray-800">{activeStoreId}</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Last Sync</span>
            <span className="font-semibold text-gray-800">{new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Fee Info */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center mb-4">
          <SettingsIcon className="text-purple-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Amazon Fee Structure (FBM UK)</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Referral Fee (Standard)</span>
            <span className="font-semibold text-gray-800">15%</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Referral Fee (Electronics)</span>
            <span className="font-semibold text-gray-800">8%</span>
          </div>
          <div className="flex justify-between p-3 bg-orange-50 rounded-lg">
            <span className="text-gray-700 font-medium">Fulfillment Fee (FBM estimate)</span>
            <span className="font-semibold text-orange-600">£3.22 – £4.75+</span>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-4">
          ℹ️ Fees vary by category. Rates are estimates for UK marketplace.
        </p>
      </div>
    </div>
  );
}

export default AmazonSettings;